import React, { Component } from "react"

// components
import {
    enumGenericFilter,
    searchifySelectGenericFilter,
    toggleGenericFilter,
} from "shared/search/containers/Filter/FilterCreators"

import {
    textGenericFilter,
    createEnumToggleMultiFilter,
    createRegionFilter,
} from "shared/search/containers/Filter/Filters"
import {
    ElectionYearSelectFilter,
    PacClassicPacLimitTypeSelect,
    PacClassicPartyTypeSelect,
} from "app/static/frontend/selects/containers/EnumSelect"
import {
    PacClassicPacOfficeSelect,
    SupporterCampaignFinanceLegislatorSelect,
} from "app/static/frontend/selects/containers/PieSelect"
import {
    PacClassicPacOfficeFilterableListView,
    SupporterLegislatorSearchifyFLV,
} from "shared/search/containers/FilterableListView/FilterableListViews"

// helpers
import { isValidYearString } from "shared/imports/sharedHelperFunctions"
import { generateElectionYearEnumItems } from "shared/search/helperFunctions"

type Props = {
    filterComponent?: Component
    uniqueFilterId?: string
    filterKey?: string
    placeholderText?: string
    displayLabel: string
}

const createFilterComponent = (props) => {
    const FilterComponent = props.filterComponent
    return <FilterComponent {...props} />
}

export const PacClassicPacOrganizationPacClassicPacLegislatorFilter = (props: Props) =>
    createFilterComponent({ ...props, searchifyFLV: props["searchifyFLV"] || SupporterLegislatorSearchifyFLV })
PacClassicPacOrganizationPacClassicPacLegislatorFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    dataTypeLabel: "Official (PAC)",
    filterKey: "official_search",
    searchifyFLV: SupporterLegislatorSearchifyFLV,
    selectComponent: SupporterCampaignFinanceLegislatorSelect,
    uniqueFilterId: "pacClassicPacOrganizationPacClassicPacLegislatorFilter",
    displayLabel: "Official",
}

export const PacClassicPacOrganizationCandidateLastNameFilter = (props: Props) => createFilterComponent(props)
PacClassicPacOrganizationCandidateLastNameFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "pacClassicPacOrganizationCandidateLastNameFilter",
    filterKey: "candidate_last_name_search",
    placeholderText: "Search by Candidate Last Name...",
    displayLabel: "Candidate Last Name",
}

export const PacClassicPacOrganizationPacClassicPacOfficeFilter = (props: Props) =>
    createFilterComponent({ ...props, searchifyFLV: props["searchifyFLV"] || PacClassicPacOfficeFilterableListView })
PacClassicPacOrganizationPacClassicPacOfficeFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    dataTypeLabel: "Offices (PAC)",
    filterKey: "related_offices",
    searchifyFLV: PacClassicPacOfficeFilterableListView,
    selectComponent: PacClassicPacOfficeSelect,
    uniqueFilterId: "pacClassicPacOrganizationPacClassicPacOfficeFilter",
    displayLabel: "Office Name",
}

export const PacClassicPacOrganizationCandidateDistrictFilter = (props: Props) => createFilterComponent(props)
PacClassicPacOrganizationCandidateDistrictFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "pacClassicPacOrganizationCandidateDistrictFilter",
    filterKey: "office_district_search",
    displayLabel: "District",
}

export const PacClassicPacOrganizationElectionYearFilter = (props: Props) => createFilterComponent(props)
PacClassicPacOrganizationElectionYearFilter.defaultProps = {
    filterComponent: enumGenericFilter,
    uniqueFilterId: "pacClassicPacOrganizationElectionYearFilter",
    filterKey: "reelection_year__in",
    enumItems: generateElectionYearEnumItems({}),
    selectComponent: ElectionYearSelectFilter,
    displayLabel: "Other Election Year",
    placeholderText: "Enter a year...",
    keyboardType: "numeric",
    shouldValidateInput: true,
    validateTextInput: isValidYearString,
    errorMessage: "Please enter a valid year.",
    // disambiguate during rendering, since uniqueFilterId is shared
    componentKey: "others",
}

export const PacClassicPacOrganizationIsIncumbentFilter = (props: Props) => createFilterComponent(props)
PacClassicPacOrganizationIsIncumbentFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "pacClassicPacOrganizationIsIncumbentFilter",
    filterKey: "incumbent",
    trueText: "Incumbent",
    falseText: "Challenger",
    displayLabel: "Incumbent Status",
}

export const PacClassicPacOrganizationPacLimitTypeMultiFilter = createEnumToggleMultiFilter({
    uniqueFilterId: "PacClassicPacOrganizationPacLimitTypeMultiFilter",
    displayLabel: "PAC Limit Type",
    filterKeyMapping: {
        true: "pac_limit_type__in",
        false: "pac_limit_type_not_in",
    },
    toggleTextMapping: {
        trueText: "Limit Type Linked",
        falseText: "Limit Type Not Linked",
    },
    selectComponent: PacClassicPacLimitTypeSelect,
    enumSelectItems: DjangIO.app.pac_classic.types.PacClassicPacLimitType.region_supported_items(),
    helpText: "Filter by Entity Type of counterparty.",
})

export const PacClassicPacOrganizationPacPartyTypeMultiFilter = createEnumToggleMultiFilter({
    uniqueFilterId: "pacClassicPacOrganizationPacPartyTypeMultiFilter",
    displayLabel: "Political party",
    filterKeyMapping: { true: "party__in", false: "party_not_in" },
    toggleTextMapping: {
        trueText: "Party Linked",
        falseText: "Party Not Linked",
    },
    selectComponent: PacClassicPartyTypeSelect,
    selectDisplayLabel: "Other political party",
    enumItems: [
        DjangIO.app.pac_classic.types.PacClassicPartyType.democrat_party,
        DjangIO.app.pac_classic.types.PacClassicPartyType.independent,
        DjangIO.app.pac_classic.types.PacClassicPartyType.republican_party,
    ],
    enumSelectItems: DjangIO.app.pac_classic.types.PacClassicPartyType.region_supported_items().filter(
        (pacClassicPartyType) =>
            !(
                [
                    DjangIO.app.pac_classic.types.PacClassicPartyType.democrat_party.value,
                    DjangIO.app.pac_classic.types.PacClassicPartyType.independent.value,
                    DjangIO.app.pac_classic.types.PacClassicPartyType.republican_party.value,
                ] as string[]
            ).includes(pacClassicPartyType.value),
    ),
    helpText: "Filter by Party.",
})

export const PacClassicPacOrganizationIsActiveFilter = (props) => createFilterComponent(props)
PacClassicPacOrganizationIsActiveFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    displayLabel: "Is Active",
    falseText: "Not Active",
    filterKey: "is_active",
    trueText: "Active",
    uniqueFilterId: "pacClassicPacOrganizationIsActiveFilter",
}

export const PacClassicPacOrganizationFilterIdFilter = (props) => createFilterComponent(props)
PacClassicPacOrganizationFilterIdFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "pacClassicPacOrganizationFilterIdFilter",
    filterKey: "filer_id",
    displayLabel: "Filer ID",
}

export const PacClassicPacOfficeStateFilter = createRegionFilter({ displayLabel: "Office State" }, "for_states")
